import React, { useMemo } from 'react';
import { Form, Input, Button, Typography, Alert, Divider, Progress } from 'antd';
import { CheckCircleOutlined, CloseCircleOutlined } from '@ant-design/icons';

const { Text } = Typography;
const ChangePasswordForm = ({
    onSubmit,
    loading = false,
    error = null,
    success = false,
    successMessage = 'Tu contraseña se actualizó correctamente.',
    disabled = false,
    showCurrentPassword = false,
    title = 'Cambiar contraseña',
}) => {
    const [form] = Form.useForm();

    const validatePassword = (_, value) => {
        if (!value) {
            return Promise.reject(new Error('Por favor, ingresa la contraseña.'));
        }

        const errors = [];

        // Mínimo 8 caracteres
        if (value.length < 8) {
            errors.push('Al menos 8 caracteres');
        }

        // Al menos una mayúscula
        if (!/[A-Z]/.test(value)) {
            errors.push('Al menos una letra mayúscula');
        }

        // Al menos una minúscula
        if (!/[a-z]/.test(value)) {
            errors.push('Al menos una letra minúscula');
        }

        // Al menos un número
        if (!/[0-9]/.test(value)) {
            errors.push('Al menos un número');
        }

        // Al menos un carácter especial
        if (!/[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(value)) {
            errors.push('Al menos un carácter especial (!@#$%^&*...)');
        }

        if (errors.length > 0) {
            return Promise.reject(
                new Error(`La contraseña debe cumplir: ${errors.join(', ')}`)
            );
        }

        return Promise.resolve();
    };

    const getPasswordStrength = (password) => {
        if (!password) return { score: 0, status: 'exception', text: '' };

        let score = 0;
        const checks = {
            length: password.length >= 8,
            uppercase: /[A-Z]/.test(password),
            lowercase: /[a-z]/.test(password),
            number: /[0-9]/.test(password),
            special: /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(password),
        };

        Object.values(checks).forEach((check) => {
            if (check) score++;
        });

        if (score <= 2) return { score, status: 'exception', text: 'Débil' };
        if (score <= 4) return { score, status: 'active', text: 'Media' };
        return { score, status: 'success', text: 'Fuerte' };
    };

    const passwordValue = Form.useWatch('password', form);
    const passwordStrength = useMemo(
        () => getPasswordStrength(passwordValue),
        [passwordValue]
    );

    const passwordRequirements = [
        { key: 'length', label: 'Al menos 8 caracteres', test: (pwd) => pwd && pwd.length >= 8 },
        { key: 'uppercase', label: 'Al menos una letra mayúscula', test: (pwd) => pwd && /[A-Z]/.test(pwd) },
        { key: 'lowercase', label: 'Al menos una letra minúscula', test: (pwd) => pwd && /[a-z]/.test(pwd) },
        { key: 'number', label: 'Al menos un número', test: (pwd) => pwd && /[0-9]/.test(pwd) },
        { key: 'special', label: 'Al menos un carácter especial', test: (pwd) => pwd && /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(pwd) },
    ];

    const handleFinish = (values) => {
        if (onSubmit) {
            onSubmit(values);
        }
    };

    return (
        <div>
            <Typography.Title level={2} style={{ textAlign: 'center', marginBottom: 24 }}>
                {title}
            </Typography.Title>

            {error && (
                <Alert
                    type="error"
                    showIcon
                    style={{ marginBottom: 16 }}
                    message={error}
                />
            )}

            {success && (
                <Alert
                    type="success"
                    showIcon
                    style={{ marginBottom: 16 }}
                    message={successMessage}
                />
            )}

            <Form
                form={form}
                layout="vertical"
                onFinish={handleFinish}
                disabled={disabled}
                autoComplete="off"
            >
                {showCurrentPassword && (
                    <Form.Item
                        label="Contraseña actual"
                        name="currentPassword"
                        rules={[
                            { required: true, message: 'Por favor, ingresa tu contraseña actual.' },
                        ]}
                    >
                        <Input.Password
                            size="large"
                            autoComplete="current-password"
                            placeholder="Ingresa tu contraseña actual"
                        />
                    </Form.Item>
                )}

                <Form.Item
                    label="Nueva contraseña"
                    name="password"
                    rules={[{ validator: validatePassword }]}
                >
                    <Input.Password
                        size="large"
                        autoComplete="new-password"
                        placeholder="Ingresa la nueva contraseña"
                    />
                </Form.Item>

                {passwordValue && (
                    <div style={{ marginBottom: 16 }}>
                        <Progress
                            percent={(passwordStrength.score / 5) * 100}
                            status={passwordStrength.status}
                            showInfo={false}
                            style={{ marginBottom: 8 }}
                        />
                        <Text type="secondary" style={{ fontSize: 12 }}>
                            Fortaleza: <strong>{passwordStrength.text}</strong>
                        </Text>
                        <div style={{ marginTop: 12 }}>
                            {passwordRequirements.map((req) => {
                                const isValid = req.test(passwordValue);
                                return (
                                    <div
                                        key={req.key}
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            marginBottom: 4,
                                            fontSize: 12,
                                        }}
                                    >
                                        {isValid ? (
                                            <CheckCircleOutlined style={{ color: '#52c41a', marginRight: 8 }} />
                                        ) : (
                                            <CloseCircleOutlined style={{ color: '#ff4d4f', marginRight: 8 }} />
                                        )}
                                        <Text type={isValid ? 'success' : 'secondary'}>{req.label}</Text>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}

                <Form.Item
                    label="Confirmar contraseña"
                    name="confirmPassword"
                    dependencies={['password']}
                    rules={[
                        { required: true, message: 'Por favor, confirma la contraseña.' },
                        ({ getFieldValue }) => ({
                            validator(_, value) {
                                if (!value || getFieldValue('password') === value) {
                                    return Promise.resolve();
                                }
                                return Promise.reject(new Error('Las contraseñas no coinciden.'));
                            },
                        }),
                    ]}
                >
                    <Input.Password
                        size="large"
                        autoComplete="new-password"
                        placeholder="Vuelve a escribir la contraseña"
                    />
                </Form.Item>

                <Divider style={{ margin: '16px 0 24px' }} />

                <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
                    <Button
                        type="primary"
                        htmlType="submit"
                        size="large"
                        loading={loading}
                        disabled={disabled}
                    >
                        Guardar nueva contraseña
                    </Button>
                </Form.Item>
            </Form>
        </div>
    );
};

export default ChangePasswordForm;

